import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { FtoReportSimilarRepository } from '../similar-repository';
import type { MatchedTechFeature, PatentProfileWithMatchedTechFeature } from '../../types';
import { FtoReportUpdateSimilarListApi, type UpdateNoveltySimilarFinalResultDto } from '../../apis/update-novelty-similar-list-api';
import { FtoReportFeatureComparisonApi } from '../../apis/novelty-feature-comparison-api';
import type { ComparisonFeature, FinalResult } from '../../apis/search-novelty-result-api';

@injectable()
export class FtoReportSimilarRepositoryImpl implements FtoReportSimilarRepository {
    constructor(
        @inject(FtoReportUpdateSimilarListApi)
        private readonly _updateNoveltySimilarListApi: FtoReportUpdateSimilarListApi,
        @inject(FtoReportFeatureComparisonApi)
        private readonly _noveltyFeatureComparisonApi: FtoReportFeatureComparisonApi,
    ) { }

    async updateNoveltySimilarList(taskId: string, techFeatures: PatentProfileWithMatchedTechFeature[]): Promise<PatentProfileWithMatchedTechFeature[]> {
        return this._updateNoveltySimilarListApi.updateNoveltySimilarList({
            task_id: taskId,
            final_result: this._convertTechFeature2FinalResult(techFeatures),
        }).then((response) => {
            return this._convertFinalResult2TechFeature(response);
        });
    }

    private _convertTechFeature(feature: ComparisonFeature) {
        return {
            techFeature: feature.tech_feature,
            comparisonFeature: feature.comparison_feature,
            score: feature.score,
            publicScore: feature.public_score,
            similar: feature.similar,
            claimNum: feature.claim_num,
            claimType: feature.claim_type,
        } as MatchedTechFeature;
    }

    private _convertTechFeature2FinalResult(techFeatures: PatentProfileWithMatchedTechFeature[]): UpdateNoveltySimilarFinalResultDto[] {
        return techFeatures.map((techFeature) => ({
            patent_id: techFeature.patentId,
            features: techFeature.matchedTechFeatures.map((feature) => ({
                comparison_feature: feature.comparisonFeature,
                tech_feature: feature.techFeature,
                similar: feature.similar,
                claim_num: feature.claimNum,
                claim_type: feature.claimType,
            })),
            most_similar: techFeature.mostSimilar ?? false,
            selected: techFeature.selected,
            comparison_conclusion: techFeature.comparisonConclusion,
            risk_level: techFeature.riskLevel,
            claim_equivalent_info: (techFeature.claimEquivalentInfo || []).map(info => ({
                claim_num: info.claimNum,
                infringement: info.infringement,
            })),
            independent_claim_num: techFeature.independentClaimNum,
            equivalent_independent_claim_num: techFeature.equivalentIndependentClaimNum,
            dependent_claim_num: techFeature.dependentClaimNum,
            equivalent_dependent_claim_num: techFeature.equivalentDependentClaimNum,
            claims: (techFeature.claims || []).map((claim) => ({
                claim_num: claim.claimNum,
                claim_text: claim.claimText,
            })),
            user_added: techFeature.userAdded,
            folder_id: techFeature.folderId,
            workspace_added: techFeature.workspaceAdded,
        }));
    }

    private _convertFinalResult2TechFeature(data: FinalResult[]): PatentProfileWithMatchedTechFeature[] {
        const result: PatentProfileWithMatchedTechFeature[] = [];
        data?.forEach((item) => {
            result.push({
                patentId: item.patent_id,
                matchedTechFeatures: item.features
                    .map((feature) => this._convertTechFeature(feature)),
                selected: item.selected,
                mostSimilar: item.most_similar,
                comparativeLiteratureType: item.comparative_literature_type,
                lang: item.lang,
                comparisonConclusion: item.comparison_conclusion,
                riskLevel: item.risk_level as any,
                claimEquivalentInfo: (item.claim_equivalent_info || []).map((info) => {
                    return {
                        claimNum: info.claim_num,
                        infringement: info.infringement,
                    };
                }),
                independentClaimNum: item.independent_claim_num,
                equivalentIndependentClaimNum: item.equivalent_independent_claim_num,
                dependentClaimNum: item.dependent_claim_num,
                equivalentDependentClaimNum: item.equivalent_dependent_claim_num,
                userAdded: item.user_added,
                folderId: item.folder_id,
                workspaceAdded: item.workspace_added,
                claims: (item.claims || []).map((claim) => ({
                    claimNum: claim.claim_num,
                    claimText: claim.claim_text,
                })),
            });
        });
        return result;
    }

    async getFeatureComparison(task_id: string, pns: string[]): Promise<PatentProfileWithMatchedTechFeature[]> {
        const result = await this._noveltyFeatureComparisonApi.getFeatureComparison({
            task_id,
            patent_pns: pns,
        });
        return (result?.final_res || []).map(p => {
            const features = p.features.map(feat => Object.assign(this._convertTechFeature(feat), { similar: true }));
            return {
                patentId: p.patent_id,
                matchedTechFeatures: features,
                selected: false,
                mostSimilar: false,
                comparativeLiteratureType: p.comparativeLiteratureType!,
                lang: p.lang,
                comparisonConclusion: p.comparison_conclusion,
            };
        });
    }
}
